import React, { useState, useEffect } from "react";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from 'prop-types';
import { customValidators, object, string, ref } from '../../../Helpers/Validators/customValidators';
import { SelectInput, TextInput } from "../../../Helpers/Inputs";
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import { InputMaskPhone } from '../../../Helpers/Formatters';
import Alert from '@material-ui/lab/Alert';
import Step1Icon from '../icons/step_1.svg';
import IconHolder from "../../../Helpers/IconHolder";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  colorPrimary: {
    color : theme.palette.primary.main,
  },
  bgColor:{
    backgroundColor: `${theme.palette.primary.main}16`,
  } 
}));

export default function PersonalInfo(props) {
  const { register, errors, setValue, triggerValidation, onFormComplete } = props;

  const [validFields, setValidFields] = useState({
    pin: false,
    pinConfirm: false,
    firstName: false,
    lastName: false,
    insuredPhoneType: false,
    insuredPhoneNumber: false,
    emailAddress: false,
  });

  const validateForm = async (field) => {
    setValidFields({
      ...validFields, 
      [field]: await triggerValidation(field),
    });
  }

  useEffect(() => {
    if (Object.values(validFields).reduce((a, b) => a && b, true) && onFormComplete) {
      onFormComplete();
    }
  }, [validFields]);

  const classes = useStyles();

  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
    if (field === 'insuredPhoneType') {
      validateForm('insuredPhoneType');
    }
  };

  useEffect(() => {
    if (props.basicInfo.email) {
      handleValueChange('emailAddress', props.basicInfo.email);
    }
  }, []);

  return (
      <React.Fragment>
          <Row className="justify-content-center">
          <IconHolder icon={Step1Icon} viewBox="0 0 25 25" />
          </Row>
          <h4 className="headerTitle">Personalized Account Set Up</h4>
          <h5 className="headerSubtitle">Please complete the information below to begin the secured and confidential application process.</h5>
          <br />
          <div>
              Enter and confirm a PIN to be able to return to your session. All information is secure and confidential.
          </div>
            <Row>
              <Col>
                <Form.Group>
                  <TextInput
                    type="password"
                    name="pin"
                    id="pin"
                    label="4 Digit PIN"
                    value={props.formState.pin}
                    onChange={e => props.onFieldChange('pin', e.target.value)}
                    onBlur={() => validateForm('pin')}
                    errors={errors.pin}
                    inputRef={register}
                    inputProps={{
                      maxLength: 4,
                    }}
                  />
                </Form.Group>
              </Col>
              <Col>
                <TextInput
                  type="password"
                  name="pinConfirm"
                  id="pinConfirm"
                  label="Confirm your PIN"
                  value={props.formState.pinConfirm}
                  onChange={e => props.onFieldChange('pinConfirm', e.target.value)}
                  onBlur={() => validateForm('pinConfirm')}
                  errors={errors.pinConfirm}
                  inputRef={register}
                  inputProps={{
                    maxLength: 4,
                  }}
                />
              </Col>
            </Row>

            <Form.Group>
              <TextInput
                name="firstName"
                id="firstName"
                label="First Name"
                value={props.formState.firstName}
                onChange={e => props.onFieldChange('firstName', e.target.value)}
                onBlur={() => validateForm('firstName')}
                errors={errors.firstName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="middleInitial"
                id="middleInitial"
                label="Middle Initial"
                value={props.formState.middleInitial}
                onChange={e => props.onFieldChange('middleInitial', e.target.value)}
                errors={errors.middleInitial}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="lastName"
                id="lastName"
                label="Last Name"
                value={props.formState.lastName}
                onChange={e => props.onFieldChange('lastName', e.target.value)}
                onBlur={() => validateForm('lastName')}
                errors={errors.lastName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <SelectInput
                name="insuredPhoneType"
                id="insuredPhoneType"
                label="Phone Number Type"
                value={props.formState.insuredPhoneType}
                selectOptions={[{ value: "home", label: "Home" }, { value: "cell", label: "Cell" }, {
                  value: "work",
                  label: "Work"
                }]}
                onChange={e => handleValueChange('insuredPhoneType', e.target.value)}
                errors={errors.insuredPhoneType}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="insuredPhoneNumber"
                id="insuredPhoneNumber"
                label="Phone Number"
                value={props.formState.insuredPhoneNumber}
                onChange={e => props.onFieldChange('insuredPhoneNumber', e.target.value)}
                onBlur={() => validateForm('insuredPhoneNumber')}
                errors={errors.insuredPhoneNumber}
                inputRef={register}
                InputProps={{
                  inputComponent: InputMaskPhone,
                }}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="emailAddress"
                id="emailAddress"
                label="Email Address"
                value={props.formState.emailAddress}
                onChange={e => props.onFieldChange('emailAddress', e.target.value)}
                onBlur={() => validateForm('emailAddress')}
                errors={errors.emailAddress}
                inputRef={register}
              />
            </Form.Group>

            <Alert className={`mt-3 mb-3 ${classes.bgColor}`} icon={false} >
              <EmojiObjectsIcon color="primary"/> <span className={`${classes.colorPrimary}`}> Insider Tip: </span> This site is completely secure through 256bit TLS encryption. All necessary precautions and technologies have been put in place to protect our customers and their personal information.
            </Alert>
      </React.Fragment>
  );
}

PersonalInfo.ValidationSchema = object().shape({
  pin: string()
    .required('PIN is required.')
    .length(4)
    .matches(/\d{4}/, 'PIN must be 4 numeric digits.'),
  pinConfirm: string()
    .required('PIN confirm is required.')
    .oneOf([ref('pin')], 'PINs must match.'),
  firstName: customValidators.string()
    .isValidNameLimitedSpecialChar('First name is required.', true, false),
  middleInitial: string()
    .matches(/^$|^(?!\s*$)[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]{1}$/, 'Must be single alpha character.')
    .notRequired(),
  lastName: customValidators.string()
    .isValidNameLimitedSpecialChar('Last name is required.', true, false),
  insuredPhoneType: string().required('Phone Number Type is required.'),
  insuredPhoneNumber: customValidators.string()
    .required('Phone Number is required.')
    .isValidPhone('Phone Number must be formatted as "(123) 456-7890".'),
  emailAddress: string()
    .email('Invalid email')
    .required('Email is required.'),
});

PersonalInfo.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    pin: prefill ? "1111" : "",
    pinConfirm: prefill ? "1111" : "",
    firstName: prefill ? "Proposed" : (basicInfo && basicInfo.firstName) || '',
    middleInitial: prefill ? "N" : '',
    lastName: prefill ? "Sured" : (basicInfo && basicInfo.lastName) || '',
    insuredPhoneType: prefill ? "home" : "",
    insuredPhoneNumber: prefill ? "(769) 345-4567" : (basicInfo && basicInfo.phone) || '',
    emailAddress: prefill ? 'user@address.com' : (basicInfo && basicInfo.email) || '',
  };
};

PersonalInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  register: PropTypes.func,
  errors: PropTypes.object,
  setValue: PropTypes.func,
  triggerValidation: PropTypes.func,
  basicInfo: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/components/Quoters/AmericoLoanProtection/steps/PersonalInfo.js